/**
 * "Continue with Google" through Firebase Auth (2026-09-25).
 *
 * The browser only gets a Firebase ID token here; the site's own session is
 * still the JWT that POST /api/auth/google answers with (the server checks the
 * token's signature, project and provider itself). So Firebase keeps nothing:
 * in-memory persistence, and a sign-out as soon as the token is read.
 *
 * The SDK (~33 kB gzipped) is only ever imported dynamically, so it lands in
 * its own async chunk and never in the entry chunk that every page loads.
 * Keep it that way: no static `import ... from 'firebase/...'` anywhere.
 *
 * Popups: Safari and Firefox refuse a window.open that is not inside the tap
 * that asked for it, and a network wait in between (loading the SDK) is
 * enough to lose the tap. So the SDK is loaded ahead of time
 * (prepareGoogleSignIn: when the button scrolls into view, on hover or on
 * focus), and getGoogleIdToken calls signInWithPopup in the same tick as the
 * tap whenever it is ready. The resolver goes in initializeAuth's deps on
 * purpose: on phones and Safari that makes the SDK set up its auth iframe
 * during the warm-up, so the tap has nothing left to wait for.
 */
import type { Auth, GoogleAuthProvider, UserCredential } from 'firebase/auth';
import { FIREBASE_WEB_CONFIG } from '@/lib/firebase-config';

type FirebaseAuthModule = typeof import('firebase/auth');

/** Our own name for the app, so it can never clash with another Firebase app on the page. */
const FIREBASE_APP_NAME = 'faceshot-google';

export type GoogleSignInErrorCode =
  | 'cancelled'
  | 'popup_blocked'
  | 'unauthorized_domain'
  | 'network'
  | 'unavailable'
  | 'account_exists'
  | 'unknown';

export class GoogleSignInError extends Error {
  readonly code: GoogleSignInErrorCode;
  /** Firebase's own code (auth/...), when there was one. For logs only. */
  readonly firebaseCode: string | null;
  /**
   * The popup was blocked because the SDK was still loading when the visitor
   * tapped, so the tap had expired by the time the window could open. The
   * SDK is ready now: a second tap works.
   */
  readonly tapAgain: boolean;

  constructor(
    code: GoogleSignInErrorCode,
    { firebaseCode = null, tapAgain = false, cause }: { firebaseCode?: string | null; tapAgain?: boolean; cause?: unknown } = {},
  ) {
    super(firebaseCode ? `${code} (${firebaseCode})` : code);
    this.name = 'GoogleSignInError';
    this.code = code;
    this.firebaseCode = firebaseCode;
    this.tapAgain = tapAgain;
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

const FIREBASE_CODE_MAP: Record<string, GoogleSignInErrorCode> = {
  'auth/popup-closed-by-user': 'cancelled',
  'auth/cancelled-popup-request': 'cancelled',
  'auth/user-cancelled': 'cancelled',
  'auth/popup-blocked': 'popup_blocked',
  'auth/unauthorized-domain': 'unauthorized_domain',
  'auth/network-request-failed': 'network',
  'auth/operation-not-allowed': 'unavailable',
  'auth/internal-error': 'unavailable',
  'auth/web-storage-unsupported': 'unavailable',
  'auth/operation-not-supported-in-this-environment': 'unavailable',
  // (2026-09-25 review) Every password signup also makes a Firebase
  // email/password account for its address (the server sends the
  // verification email through it). Under Firebase's default "one account
  // per email", a later Google sign-in on that address with a Google account
  // Firebase doesn't count as authoritative for it (Google on a hotmail /
  // yahoo / company address) fails with this, every time. "Try again" would
  // be a dead end: the visitor has to use their password.
  'auth/account-exists-with-different-credential': 'account_exists',
};

/** Firebase's error code (auth/...) as one of ours. */
export function googleSignInErrorCode(firebaseCode: unknown): GoogleSignInErrorCode {
  return (typeof firebaseCode === 'string' && FIREBASE_CODE_MAP[firebaseCode]) || 'unknown';
}

export function toGoogleSignInError(err: unknown, { tapAgain = false }: { tapAgain?: boolean } = {}): GoogleSignInError {
  if (err instanceof GoogleSignInError) return err;
  const raw = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  const firebaseCode = typeof raw === 'string' ? raw : null;
  const code = googleSignInErrorCode(firebaseCode);
  return new GoogleSignInError(code, { firebaseCode, tapAgain: tapAgain && code === 'popup_blocked', cause: err });
}

interface GoogleSession {
  mod: FirebaseAuthModule;
  auth: Auth;
  provider: GoogleAuthProvider;
}

let ready: GoogleSession | null = null;
let preparing: Promise<GoogleSession> | null = null;

function loadSession(): Promise<GoogleSession> {
  if (ready) return Promise.resolve(ready);
  if (preparing) return preparing;

  const attempt = (async () => {
    let appMod: typeof import('firebase/app');
    let authMod: FirebaseAuthModule;
    try {
      [appMod, authMod] = await Promise.all([import('firebase/app'), import('firebase/auth')]);
    } catch (err) {
      // The chunk did not load (offline, or a deploy replaced it).
      throw new GoogleSignInError('unavailable', { cause: err });
    }
    const app = appMod.getApps().find((a) => a.name === FIREBASE_APP_NAME)
      ?? appMod.initializeApp(FIREBASE_WEB_CONFIG, FIREBASE_APP_NAME);
    // A retry after a failed warm-up gets the same instance back (same deps).
    const auth = authMod.initializeAuth(app, {
      persistence: authMod.inMemoryPersistence,
      popupRedirectResolver: authMod.browserPopupRedirectResolver,
    });
    await auth.authStateReady();
    const provider = new authMod.GoogleAuthProvider();
    // Always the account chooser: a shared computer must not sign the next
    // visitor straight into whoever used Google on it last.
    provider.setCustomParameters({ prompt: 'select_account' });
    ready = { mod: authMod, auth, provider };
    return ready;
  })();

  preparing = attempt;
  // A failed warm-up is forgotten, so the next hover or tap tries again.
  attempt.catch(() => {
    if (preparing === attempt) preparing = null;
  });
  return attempt;
}

/** Loads the SDK ahead of the tap. Safe to call any number of times. */
export function prepareGoogleSignIn(): Promise<void> {
  return loadSession().then(() => undefined, (err) => {
    throw toGoogleSignInError(err);
  });
}

/** True once a tap can open the Google popup with nothing left to load. */
export function isGoogleSignInReady(): boolean {
  return ready !== null;
}

function openPopup(session: GoogleSession): Promise<UserCredential> {
  try {
    return session.mod.signInWithPopup(session.auth, session.provider);
  } catch (err) {
    return Promise.reject(err);
  }
}

async function readIdToken(session: GoogleSession, pending: Promise<UserCredential>, afterWait: boolean): Promise<string> {
  let cred: UserCredential;
  try {
    cred = await pending;
  } catch (err) {
    throw toGoogleSignInError(err, { tapAgain: afterWait });
  }
  try {
    return await cred.user.getIdToken();
  } catch (err) {
    throw toGoogleSignInError(err);
  } finally {
    // The site keeps its own session; Firebase's copy is done once the token is read.
    session.mod.signOut(session.auth).catch(() => undefined);
  }
}

/**
 * Opens Google's account chooser and resolves with a Firebase ID token for
 * POST /api/auth/google. Call it straight from the click handler, before any
 * await or state update: when the SDK is ready the popup opens in this very
 * call. Rejects with a GoogleSignInError, never throws synchronously.
 */
export function getGoogleIdToken(): Promise<string> {
  if (ready) {
    const session = ready;
    return readIdToken(session, openPopup(session), false);
  }
  // Not warmed up (a tap within a moment of the page opening): load, then
  // try. The tap may have expired by then; a blocked popup says "tap again".
  return loadSession().then(
    (session) => readIdToken(session, openPopup(session), true),
    (err) => {
      throw toGoogleSignInError(err);
    },
  );
}

// ---------------------------------------------------------------------------
// In-app browsers. Google refuses OAuth inside embedded webviews
// (disallowed_useragent, since 2021-09-30), so a tap there must not start the
// popup at all: the button explains how to open the page in a real browser.
// Plain iOS Safari / Chrome and Android Chrome are NOT in-app browsers.
// ---------------------------------------------------------------------------

export type InAppBrowserApp =
  | 'telegram'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'line'
  | 'snapchat'
  | 'linkedin'
  | 'wechat'
  | 'webview';

export type InAppBrowserPlatform = 'android' | 'ios' | 'other';

export interface InAppBrowser {
  app: InAppBrowserApp;
  platform: InAppBrowserPlatform;
}

type NavigatorLike = { userAgent?: string; maxTouchPoints?: number } | null | undefined;

function telegramMiniAppData(win: Record<string, unknown>): boolean {
  const tg = win.Telegram as { WebApp?: { initData?: unknown } } | undefined;
  const initData = tg?.WebApp?.initData;
  return typeof initData === 'string' && initData.length > 0;
}

function inAppBrowserApp(ua: string, win: Record<string, unknown>): InAppBrowserApp | null {
  // Telegram's webviews add no UA token of their own (except Android Mini
  // Apps), but they inject these bridge globals.
  if (
    'TelegramWebview' in win
    || 'TelegramWebviewProxy' in win
    || 'TelegramWebviewProxyProto' in win
    || telegramMiniAppData(win)
    || /Telegram-Android/i.test(ua)
  ) return 'telegram';
  if (/\bInstagram/i.test(ua)) return 'instagram';
  if (/\bFB[\w_]+\/|\bFacebook/i.test(ua) || /\bFBAN|\bFBAV/.test(ua)) return 'facebook';
  if (/musical_ly|Bytedance/i.test(ua)) return 'tiktok';
  if (/\bLine\//i.test(ua)) return 'line';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  if (/LinkedInApp/i.test(ua)) return 'linkedin';
  if (/MicroMessenger/i.test(ua)) return 'wechat';
  // Any other Android WebView marks itself with "; wv)".
  if (/; wv\)/.test(ua)) return 'webview';
  return null;
}

function inAppBrowserPlatform(ua: string, nav: NavigatorLike): InAppBrowserPlatform {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS asks for desktop pages with a Mac user agent; the touch points give it away.
  if (/Macintosh/i.test(ua) && (nav?.maxTouchPoints ?? 0) > 1) return 'ios';
  return 'other';
}

export function detectInAppBrowser(
  nav: NavigatorLike = typeof navigator === 'undefined' ? undefined : navigator,
  win: unknown = typeof window === 'undefined' ? undefined : window,
): InAppBrowser | null {
  const ua = String(nav?.userAgent || '');
  const scope = (win && typeof win === 'object' ? win : {}) as Record<string, unknown>;
  const app = inAppBrowserApp(ua, scope);
  if (!app) return null;
  return { app, platform: inAppBrowserPlatform(ua, nav) };
}

/**
 * An Android link that opens this page in Chrome instead of the app's
 * webview. Always https: Google sign-in only runs on the real site anyway.
 */
export function androidChromeIntentUrl(href: string): string {
  let rest: string;
  try {
    const url = new URL(href);
    rest = `${url.host}${url.pathname}${url.search}${url.hash}`;
  } catch {
    rest = String(href || '').replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  }
  return `intent://${rest}#Intent;scheme=https;package=com.android.chrome;end`;
}
